(() => {
  if (window.customElements.get('new-multi-column')) {
    return;
  }

  class NewMultiColumn extends HTMLElement {
    connectedCallback() {
      if (this._initialized) {
        this.refresh();
        return;
      }

      this._initialized = true;
      this.multiColumnInner = this.querySelector('.multi-column__inner');
      this.desktopPrevButton = this.querySelector('.prev-next-button--prev');
      this.desktopNextButton = this.querySelector('.prev-next-button--next');
      this.mobilePrevButton = this.querySelector('.product-list__button-prev');
      this.mobileNextButton = this.querySelector('.product-list__button-next');
      this.mobileSwiperBox = this.querySelector('.swiper-container');

      this.onDesktopPrevClick = this.previous.bind(this);
      this.onDesktopNextClick = this.next.bind(this);
      this.onDesktopScroll = this.updateDesktopButtons.bind(this);
      this.onResize = this.refresh.bind(this);

      if (this.desktopPrevButton) {
        this.desktopPrevButton.addEventListener('click', this.onDesktopPrevClick);
      }

      if (this.desktopNextButton) {
        this.desktopNextButton.addEventListener('click', this.onDesktopNextClick);
      }

      if (this.multiColumnInner) {
        this.multiColumnInner.addEventListener('scroll', this.onDesktopScroll);
      }

      if (this.hasAttribute('stagger-apparition')) {
        this.setupVisibility();
      } else {
        this.revealItemsImmediately();
      }

      window.addEventListener('resize', this.onResize);
      this.refresh();
    }

    disconnectedCallback() {
      if (this.desktopPrevButton) {
        this.desktopPrevButton.removeEventListener('click', this.onDesktopPrevClick);
      }

      if (this.desktopNextButton) {
        this.desktopNextButton.removeEventListener('click', this.onDesktopNextClick);
      }

      if (this.multiColumnInner) {
        this.multiColumnInner.removeEventListener('scroll', this.onDesktopScroll);
      }

      window.removeEventListener('resize', this.onResize);
    }

    refresh() {
      this.boxObject = this.getVisibleItemCount();
      this.updateDesktopButtons();
      this.initMobileSwiper();
    }

    revealItemsImmediately() {
      this.querySelectorAll('.multi-column__item[reveal]').forEach((item) => {
        item.style.opacity = '1';
        item.style.transform = 'translateY(0)';
      });
    }

    async setupVisibility() {
      if (
        typeof this.untilVisible !== 'function' ||
        typeof window.CustomAnimation === 'undefined' ||
        typeof window.ParallelEffect === 'undefined' ||
        typeof window.CustomKeyframeEffect === 'undefined' ||
        typeof window.MediaFeatures === 'undefined'
      ) {
        this.revealItemsImmediately();
        return;
      }

      await this.untilVisible();
      const prefersReducedMotion = window.MediaFeatures.prefersReducedMotion();
      const items = Array.from(this.querySelectorAll('.multi-column__item'));
      const animation = new window.CustomAnimation(
        new window.ParallelEffect(
          items.map((item, index) => {
            return new window.CustomKeyframeEffect(
              item,
              {
                opacity: [0, 1],
                transform: [
                  `translateY(${
                    prefersReducedMotion ? 0 : window.innerWidth < 1000 ? 35 : 60
                  }px)`,
                  'translateY(0)',
                ],
              },
              {
                duration: 600,
                delay: prefersReducedMotion ? 0 : 100 * index,
                easing: 'ease',
              },
            );
          }),
        ),
      );

      animation.play();
    }

    initMobileSwiper() {
      if (!this.mobileSwiperBox || !window.Swiper || window.innerWidth > 740) {
        return;
      }

      if (this.mobileSwiperBox.swiper) {
        this.mobileSwiperBox.swiper.update();
        return;
      }

      const slides = this.mobileSwiperBox.querySelectorAll('.swiper-slide');

      new Swiper(this.mobileSwiperBox, {
        direction: 'horizontal',
        loop: slides.length > 2,
        slidesPerView: 1.45,
        centeredSlides: true,
        mousewheel: true,
        keyboard: true,
        simulateTouch: true,
        resistanceRatio: 0,
        initialSlide: 0,
        speed: 500,
        navigation: {
          nextEl: this.mobileNextButton,
          prevEl: this.mobilePrevButton,
        },
      });
    }

    getVisibleItemCount() {
      if (!this.multiColumnInner || window.innerWidth <= 740) {
        return null;
      }

      const items = Array.from(this.multiColumnInner.children).filter((item) => item.offsetParent);
      const parentRect = this.multiColumnInner.getBoundingClientRect();
      let visibleCount = 0;
      let itemWidth = 0;

      items.forEach((item) => {
        const itemRect = item.getBoundingClientRect();

        if (itemRect.left >= parentRect.left && itemRect.right <= parentRect.right) {
          visibleCount += 1;

          if (visibleCount === 1) {
            itemWidth = itemRect.width;
          }
        }
      });

      if (!visibleCount || !itemWidth) {
        return null;
      }

      return { visibleCount, itemWidth };
    }

    updateDesktopButtons() {
      if (!this.multiColumnInner || !this.desktopPrevButton || !this.desktopNextButton || window.innerWidth <= 740) {
        return;
      }

      const scrollLeft = this.multiColumnInner.scrollLeft;
      const maxScrollLeft = this.multiColumnInner.scrollWidth - this.multiColumnInner.clientWidth;
      const hasOverflow = maxScrollLeft > 2;

      this.desktopPrevButton.disabled = !hasOverflow || scrollLeft <= 2;
      this.desktopNextButton.disabled = !hasOverflow || scrollLeft >= maxScrollLeft - 2;
    }

    getDirectionFlip() {
      const direction =
        window.themeVariables?.settings?.direction ||
        document.documentElement.getAttribute('dir') ||
        'ltr';

      return direction === 'ltr' ? 1 : -1;
    }

    previous() {
      if (!this.multiColumnInner || !this.boxObject || window.innerWidth <= 740) {
        return;
      }

      const columnGap = parseInt(getComputedStyle(this).getPropertyValue('--multi-column-column-gap'), 10) || 0;
      const itemScrollWidth = this.boxObject.visibleCount * (this.boxObject.itemWidth + columnGap);

      this.multiColumnInner.scrollBy({
        left: -itemScrollWidth * this.getDirectionFlip(),
        behavior: 'smooth',
      });
    }

    next() {
      if (!this.multiColumnInner || !this.boxObject || window.innerWidth <= 740) {
        return;
      }

      const columnGap = parseInt(getComputedStyle(this).getPropertyValue('--multi-column-column-gap'), 10) || 0;
      const itemScrollWidth = this.boxObject.visibleCount * (this.boxObject.itemWidth + columnGap);

      this.multiColumnInner.scrollBy({
        left: itemScrollWidth * this.getDirectionFlip(),
        behavior: 'smooth',
      });
    }
  }

  window.customElements.define('new-multi-column', NewMultiColumn);
})();
